// Surveys & Listening (Phase 1) — HR-gated (performance:manage). Author a survey (title, privacy,
// audience, questions), publish it (snapshots the audience + notifies), and read anonymity-aware
// results. Anonymous/confidential results stay hidden until the min-N reveal threshold is met —
// the API enforces this; the UI just reflects it. A concerning result → an HR Action Center
// follow-up (source_domain=survey), never a competing action system.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, Plus, Send, BarChart3, Bell, Lock, Users, X, Trash2, Flag, Download, MessageSquareQuote } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { getApiErrorMessage } from '@/lib/apiError';
import { getDepartments } from '@/lib/hrApi';
import * as api from '@/services/performanceService';
import { usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';

const IDENTITY: { value: api.SurveyIdentityMode; label: string; hint: string }[] = [
  { value: 'ANONYMOUS', label: 'Anonymous', hint: 'You cannot see who said what. Results appear only after the reveal threshold.' },
  { value: 'CONFIDENTIAL', label: 'Confidential', hint: 'The platform tracks one-response/abuse; you see aggregates only.' },
  { value: 'NAMED', label: 'Named', hint: 'Respondent identity is visible to you (onboarding, individual dev).' },
];
const Q_TYPES: { value: api.SurveyQuestionType; label: string }[] = [
  { value: 'single', label: 'Single choice' }, { value: 'multi', label: 'Multiple choice' },
  { value: 'yesno', label: 'Yes / No' }, { value: 'rating', label: 'Rating scale' },
  { value: 'nps', label: 'NPS (0–10)' }, { value: 'numeric', label: 'Number' },
  { value: 'short_text', label: 'Short text' }, { value: 'long_text', label: 'Long text' },
];
const statusColor: Record<string, string> = {
  Draft: '#94a3b8', Scheduled: '#f59e0b', Open: '#22c55e', Paused: '#f59e0b', Closed: '#3b82f6', Archived: '#94a3b8',
};

type Draft = { type: api.SurveyQuestionType; prompt: string; required: boolean; options: string };

export const SurveysAdmin: React.FC = () => {
  const [surveys, setSurveys] = useState<api.Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [sel, setSel] = useState<api.Survey | null>(null);
  const [results, setResults] = useState<api.SurveyResults | null>(null);
  const [busy, setBusy] = useState(false);
  const promptText = usePrompt();

  // Draft form
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [identity, setIdentity] = useState<api.SurveyIdentityMode>('ANONYMOUS');
  const [scope, setScope] = useState('all');
  const [deptIds, setDeptIds] = useState<string[]>([]);
  const [depts, setDepts] = useState<{ id: string; name: string }[]>([]);
  const [qs, setQs] = useState<Draft[]>([{ type: 'rating', prompt: '', required: true, options: '' }]);

  const load = () => api.listSurveys()
    .then(setSurveys).catch((e) => toast.error(getApiErrorMessage(e, 'Could not load surveys')))
    .finally(() => setLoading(false));
  useEffect(() => { void load(); }, []);
  // Departments for audience targeting (id + name). Best-effort; falls back to company-wide.
  useEffect(() => {
    getDepartments()
      .then((r: any) => setDepts(((r?.items ?? r ?? []) as any[]).map((d) => ({ id: String(d.id), name: d.name || d.title || 'Department' }))))
      .catch(() => setDepts([]));
  }, []);
  const toggleDept = (id: string) => setDeptIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const openResults = async (s: api.Survey) => {
    setSel(s); setResults(null);
    try { setResults(await api.getSurveyResults(s.id)); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Could not load results')); }
  };

  // CSV export — aggregate results (per question: bucket, count, %), plus averages and
  // (anonymity-gated) free-text comments. Client-side download; no server round-trip.
  const exportCsv = () => {
    if (!sel || !results) return;
    const esc = (v: unknown) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows: (string | number)[][] = [
      ['Survey', sel.title],
      ['Privacy', sel.identity_mode],
      ['Responded', results.responded_count],
      ['Invited', results.invited_count],
      ['Response rate', `${Math.round(results.response_rate * 100)}%`],
      [],
      ['Question', 'Type', 'Answer / Bucket', 'Count', 'Percent'],
    ];
    (results.questions || []).forEach((q) => {
      if (q.average != null) rows.push([q.prompt, q.type, 'AVERAGE', q.average.toFixed(2), '']);
      if (q.counts) {
        Object.entries(q.counts)
          .sort((a, b) => (isNum(a[0]) && isNum(b[0]) ? Number(a[0]) - Number(b[0]) : b[1] - a[1]))
          .forEach(([k, v]) => rows.push([q.prompt, q.type, k, v, q.answered ? `${Math.round((v / q.answered) * 100)}%` : '']));
      }
      (q.comments || []).forEach((c) => rows.push([q.prompt, q.type, 'COMMENT', c, '']));
    });
    const csv = rows.map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sel.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-results.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const buildQuestions = (): api.SurveyQuestion[] => qs
    .filter((q) => q.prompt.trim())
    .map((q, i) => {
      const config: Record<string, unknown> = {};
      if (q.type === 'single' || q.type === 'multi') config.options = q.options.split(',').map((o) => o.trim()).filter(Boolean);
      if (q.type === 'rating') { config.scale_min = 1; config.scale_max = 5; }
      return { position: i, type: q.type, prompt: q.prompt.trim(), required: q.required, config };
    });

  // The draft created by an earlier attempt that failed later (questions or
  // publish): a retry updates THAT draft instead of creating another one.
  const [draftId, setDraftId] = useState<string | null>(null);
  // The editor session a save belongs to: bumped whenever the editor opens or
  // closes. A save that finishes after its editor was closed must never touch
  // the next editor — attach its draft, publish, or reset the form (round 2,
  // review HO-0mvs3oeqp001). The abandoned draft stays a draft in the list.
  const editorSession = useRef(0);
  const openEditor = () => { editorSession.current += 1; setDraftId(null); setCreating(true); };
  const closeEditor = () => { editorSession.current += 1; setCreating(false); setDraftId(null); };

  const createAndPublish = async (publish: boolean) => {
    if (!title.trim()) { toast.error('Give the survey a title'); return; }
    const questions = buildQuestions();
    if (questions.length === 0) { toast.error('Add at least one question'); return; }
    const thin = questions.findIndex((q) => (q.type === 'single' || q.type === 'multi')
      && ((q.config?.options as string[] | undefined)?.length ?? 0) < 2);
    if (thin >= 0) { toast.error(`Question ${thin + 1} needs at least two options (comma-separated)`); return; }
    if (scope === 'department' && deptIds.length === 0) { toast.error('Pick at least one department'); return; }
    setBusy(true);
    const session = editorSession.current;
    const abandoned = () => editorSession.current !== session;
    try {
      const audience_rule = scope === 'department' ? { scope: 'department', department_ids: deptIds } : { scope };
      const input = { title: title.trim(), description: desc.trim(), identity_mode: identity, audience_rule };
      const s = draftId ? await api.updateSurvey(draftId, input) : await api.createSurvey(input);
      if (abandoned()) { void load(); return; } // the editor was closed: leave it a draft
      setDraftId(s.id);
      await api.setSurveyQuestions(s.id, questions);
      if (abandoned()) { void load(); return; }
      if (publish) {
        const { invited } = await api.publishSurvey(s.id);
        toast.success(`Published — ${invited} ${invited === 1 ? 'person' : 'people'} invited`);
      } else {
        toast.success('Draft saved');
      }
      if (abandoned()) { void load(); return; }
      closeEditor(); setTitle(''); setDesc(''); setIdentity('ANONYMOUS'); setScope('all'); setDeptIds([]);
      setQs([{ type: 'rating', prompt: '', required: true, options: '' }]);
      void load();
    } catch (e) {
      if (!abandoned()) toast.error(getApiErrorMessage(e, 'Could not save the survey'));
      void load(); // a draft may exist now — show it rather than "No surveys yet"
    } finally { setBusy(false); }
  };

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try { await fn(); toast.success(ok); void load(); if (sel) void openResults(sel); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Action failed')); }
    finally { setBusy(false); }
  };

  const setQ = (i: number, patch: Partial<Draft>) => setQs((prev) => prev.map((q, j) => (j === i ? { ...q, ...patch } : q)));

  return (
    <div className="max-w-5xl">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 className="text-2xl font-bold text-charcoal">Surveys & Listening</h1>
          <p className="mt-1 text-sm text-muted-foreground">Ask your team, protect their anonymity, act on what you hear.</p>
        </div>
        {!creating && <Button onClick={openEditor}><Plus size={16} /> New survey</Button>}
      </div>

      {creating && (
        <Card style={{ marginBottom: 20 }}>
          <CardContent style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <h3 style={{ fontWeight: 700 }}>New survey</h3>
              <button type="button" aria-label="Close editor" onClick={closeEditor} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <input placeholder="Survey title" value={title} onChange={(e) => setTitle(e.target.value)}
              style={inp} />
            <textarea placeholder="Short description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)}
              style={{ ...inp, minHeight: 60 }} />
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <label style={lbl}><Lock size={13} /> Privacy
                <select value={identity} onChange={(e) => setIdentity(e.target.value as api.SurveyIdentityMode)} style={sel2}>
                  {IDENTITY.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              <label style={lbl}><Users size={13} /> Audience
                <select value={scope} onChange={(e) => setScope(e.target.value)} style={sel2}>
                  <option value="all">Entire company</option>
                  <option value="department" disabled={depts.length === 0}>Specific departments</option>
                </select>
              </label>
            </div>
            {scope === 'department' && (
              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <div className="mb-2 text-xs font-semibold text-muted-foreground">
                  Choose departments {deptIds.length > 0 && `· ${deptIds.length} selected`}
                </div>
                {depts.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No departments found.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {depts.map((d) => {
                      const on = deptIds.includes(d.id);
                      return (
                        <button key={d.id} type="button" onClick={() => toggleDept(d.id)}
                          className={`rounded-full border px-2.5 py-1 text-xs font-medium transition ${on ? 'border-primary-500 bg-primary-500/15 text-primary-700 dark:text-primary-300' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                          {d.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <p style={{ fontSize: 12, color: '#64748b' }}>{IDENTITY.find((i) => i.value === identity)?.hint}</p>

            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>Questions</span>
                <Button variant="outline" onClick={() => setQs((p) => [...p, { type: 'single', prompt: '', required: true, options: '' }])}><Plus size={14} /> Add</Button>
              </div>
              {qs.map((q, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                  <select value={q.type} onChange={(e) => setQ(i, { type: e.target.value as api.SurveyQuestionType })} style={{ ...sel2, width: 150 }}>
                    {Q_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input placeholder={`Question ${i + 1}`} value={q.prompt} onChange={(e) => setQ(i, { prompt: e.target.value })} style={inp} />
                    {(q.type === 'single' || q.type === 'multi') && (
                      <input placeholder="Options, comma-separated" value={q.options} onChange={(e) => setQ(i, { options: e.target.value })} style={{ ...inp, fontSize: 12 }} />
                    )}
                  </div>
                  <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, paddingTop: 10 }}>
                    <input type="checkbox" checked={q.required} onChange={(e) => setQ(i, { required: e.target.checked })} /> Req
                  </label>
                  {qs.length > 1 && <button onClick={() => setQs((p) => p.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', paddingTop: 10 }}><Trash2 size={15} color="#ef4444" /></button>}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button disabled={busy} onClick={() => createAndPublish(true)}><Send size={15} /> Publish now</Button>
              <Button variant="outline" disabled={busy} onClick={() => createAndPublish(false)}>Save draft</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <Loader2 className="animate-spin" /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {surveys.length === 0 && <p style={{ color: '#64748b' }}>No surveys yet. Create one to start listening.</p>}
          {surveys.map((s) => (
            <Card key={s.id}>
              <CardContent style={{ padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700 }}>{s.title}</span>
                    <Badge style={{ background: statusColor[s.status], color: '#fff' }}>{s.status}</Badge>
                    {s.identity_mode !== 'NAMED' && <Badge variant="outline"><Lock size={11} /> {s.identity_mode === 'ANONYMOUS' ? 'Anonymous' : 'Confidential'}</Badge>}
                  </div>
                  <p style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {s.responded_count}/{s.invited_count} responded{s.closes_at ? ` · closes ${new Date(s.closes_at).toLocaleDateString()}` : ''}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(s.status === 'Open' || s.status === 'Closed') && <Button variant="outline" onClick={() => openResults(s)}><BarChart3 size={14} /> Results</Button>}
                  {s.status === 'Open' && <Button variant="outline" disabled={busy} onClick={() => act(() => api.remindSurvey(s.id), 'Reminder sent to non-responders')}><Bell size={14} /></Button>}
                  {s.status === 'Open' && <Button variant="outline" disabled={busy} onClick={() => act(() => api.setSurveyStatus(s.id, 'Closed'), 'Survey closed')}>Close</Button>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {sel && results && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:p-8" onClick={() => { setSel(null); setResults(null); }}>
          <div className="w-full max-w-3xl rounded-2xl border border-border bg-card p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-bold text-charcoal">{sel.title}</h3>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${sel.identity_mode === 'ANONYMOUS' ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300' : sel.identity_mode === 'CONFIDENTIAL' ? 'bg-sky-500/15 text-sky-600 dark:text-sky-300' : 'bg-muted text-muted-foreground'}`}>
                    {sel.identity_mode === 'ANONYMOUS' && <Lock className="mr-1 inline h-2.5 w-2.5" />}{sel.identity_mode}
                  </span>
                </div>
                {sel.description && <p className="mt-0.5 text-sm text-muted-foreground">{sel.description}</p>}
              </div>
              <button onClick={() => { setSel(null); setResults(null); }} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted"><X size={18} /></button>
            </div>

            {/* Summary tiles */}
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <ResponseRing rate={results.response_rate} />
              <StatTile label="Responded" value={`${results.responded_count}`} sub={`of ${results.invited_count} invited`} />
              {/* question_count, not the aggregates: those are withheld below min-N (DEF-0mvs7c366004). */}
              <StatTile label="Questions" value={`${results.question_count ?? (results.questions || []).length}`} sub={sel.status} />
              <div className="flex flex-col justify-center">
                <Button variant="outline" size="sm" disabled={!results.revealed} onClick={exportCsv} className="w-full"><Download size={14} className="mr-1.5" /> Export CSV</Button>
                {!results.revealed && <span className="mt-1 text-center text-[10px] text-muted-foreground">Available once revealed</span>}
              </div>
            </div>

            {!results.revealed ? (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-foreground">
                <Lock size={16} className="mt-0.5 shrink-0 text-amber-600" />
                <span>Results stay hidden until at least <b>{results.min_n}</b> people respond — this protects anonymity. Currently <b>{results.responded_count}</b>.</span>
              </div>
            ) : (
              <div className="mt-5 space-y-4">
                {(results.questions || []).map((q, i) => <QResult key={q.question_id} q={q} index={i + 1} />)}
                {(results.questions || []).length === 0 && <p className="text-sm text-muted-foreground">No aggregated answers yet.</p>}
              </div>
            )}

            <div className="mt-6 flex items-center justify-between gap-3 border-t border-border pt-4">
              <span className="text-xs text-muted-foreground">A concerning result? Open an HR Action so it doesn't get lost.</span>
              <Button variant="outline" disabled={busy} onClick={async () => {
                const note = await promptText({ title: 'Create HR follow-up', message: 'This opens an HR Action Center item.', label: 'What should HR follow up on?', required: true, multiline: true, confirmLabel: 'Create follow-up' });
                if (note != null) void act(() => api.createSurveyFollowup(sel.id, `Survey follow-up: ${sel.title}`, note), 'Follow-up created in the HR Action Center');
              }}><Flag size={14} className="mr-1.5" /> Create HR follow-up</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const isNum = (s: string) => /^-?\d+(\.\d+)?$/.test(s);
const npsScore = (counts: Record<string, number>): number | null => {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  if (!total) return null;
  let prom = 0, det = 0;
  Object.entries(counts).forEach(([k, v]) => { const n = Number(k); if (n >= 9) prom += v; else if (n <= 6) det += v; });
  return Math.round((prom / total - det / total) * 100);
};

const StatTile: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <div className="rounded-xl border border-border bg-muted/30 p-3">
    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="mt-0.5 text-2xl font-bold tabular-nums text-foreground">{value}</div>
    {sub && <div className="truncate text-[11px] text-muted-foreground">{sub}</div>}
  </div>
);

const ResponseRing: React.FC<{ rate: number }> = ({ rate }) => {
  const pct = Math.max(0, Math.min(100, Math.round((rate || 0) * 100)));
  const r = 26, C = 2 * Math.PI * r;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 p-3">
      <svg viewBox="0 0 64 64" className="h-14 w-14 shrink-0 -rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" stroke="currentColor" strokeWidth="7" className="text-border" />
        <circle cx="32" cy="32" r={r} fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C * (1 - pct / 100)} className="text-primary-500" />
      </svg>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Response rate</div>
        <div className="text-2xl font-bold tabular-nums text-foreground">{pct}%</div>
      </div>
    </div>
  );
};

const QResult: React.FC<{ q: api.SurveyQuestionResult; index: number }> = ({ q, index }) => {
  const counts = q.counts || {};
  const hasCounts = Object.keys(counts).length > 0;
  const total = Object.values(counts).reduce((a, b) => a + b, 0) || q.answered || 0;
  const numeric = hasCounts && Object.keys(counts).every(isNum);
  const entries = hasCounts
    ? Object.entries(counts).sort((a, b) => (numeric ? Number(a[0]) - Number(b[0]) : b[1] - a[1]))
    : [];
  const max = Math.max(1, ...entries.map(([, v]) => v));
  const nps = q.type === 'nps' ? npsScore(counts) : null;
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm font-semibold text-foreground"><span className="text-muted-foreground">{index}.</span> {q.prompt}</div>
        <span className="shrink-0 text-[11px] text-muted-foreground">{q.answered} answered</span>
      </div>

      {(q.average != null || nps != null) && (
        <div className="mt-2 flex flex-wrap items-center gap-5">
          {q.average != null && (
            <div><span className="text-3xl font-bold text-primary-600">{q.average.toFixed(1)}</span><span className="ml-1 text-xs text-muted-foreground">avg</span></div>
          )}
          {nps != null && (
            <div><span className={`text-3xl font-bold ${nps >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{nps > 0 ? '+' : ''}{nps}</span><span className="ml-1 text-xs text-muted-foreground">NPS</span></div>
          )}
        </div>
      )}

      {hasCounts && (
        <div className="mt-3 space-y-1.5">
          {entries.map(([k, v]) => {
            const pct = total ? Math.round((v / total) * 100) : 0;
            return (
              <div key={k} className="flex items-center gap-2 text-sm">
                <span className="w-24 shrink-0 truncate text-muted-foreground" title={k}>{k}</span>
                <div className="h-5 flex-1 overflow-hidden rounded bg-muted">
                  <div className="flex h-full items-center rounded bg-primary-500 px-1.5 text-[10px] font-semibold text-white" style={{ width: `${(v / max) * 100}%` }}>{pct >= 12 ? `${pct}%` : ''}</div>
                </div>
                <span className="w-8 shrink-0 text-right tabular-nums text-muted-foreground">{v}</span>
              </div>
            );
          })}
        </div>
      )}

      {q.comments && q.comments.length > 0 && (
        <div className="mt-3 space-y-1.5">
          <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><MessageSquareQuote className="h-3 w-3" /> Comments ({q.comments.length})</div>
          {q.comments.map((c, i) => (
            <div key={i} className="rounded-lg border-l-2 border-primary-400 bg-muted/40 px-3 py-1.5 text-sm text-foreground" dir="auto">{c}</div>
          ))}
        </div>
      )}

      {!hasCounts && !q.comments?.length && q.average == null && <p className="mt-2 text-xs text-muted-foreground">No answers yet.</p>}
    </div>
  );
};

const inp: React.CSSProperties = { padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, width: '100%' };
const sel2: React.CSSProperties = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, marginLeft: 6 };
const lbl: React.CSSProperties = { fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: 32, zIndex: 50, overflowY: 'auto' };
const panel: React.CSSProperties = { background: '#fff', borderRadius: 12, padding: 24, maxWidth: 620, width: '100%' };

export default SurveysAdmin;
